import { useState, useCallback } from 'react';
import { DeliveryCheckResult } from '../types/landing';
import { getCSRFToken } from '../../../assets/ts/util/csrf';
import { route } from '../utils/route';

interface AddressData {
  lat: number;
  lng: number;
  address: string;
  is_country?: boolean;
  neighborhood?: string;
  block?: string;
  house?: string;
}

interface UseDeliveryZoneCheckReturn {
  checkDeliveryZone: (lat: number, lng: number) => Promise<DeliveryCheckResult>;
  saveGuestAddress: (addressData: AddressData) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

export function useDeliveryZoneCheck(): UseDeliveryZoneCheckReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkDeliveryZone = useCallback(async (lat: number, lng: number): Promise<DeliveryCheckResult> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(route('api.delivery_zone.check'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-XSRF-TOKEN': getCSRFToken(),
        },
        credentials: 'include',
        body: JSON.stringify({ lat, lng }),
      });

      if (!response.ok) {
        throw new Error('Error al verificar la zona de entrega');
      }

      const result = await response.json();
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error desconocido';
      setError(message);
      return {
        isDeliverable: false,
        message: 'Error al verificar la zona de entrega. Intentá de nuevo.',
        dates: [],
        zone_id: null,
        shipping_cost: null,
        minimum_order: null,
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const saveGuestAddress = useCallback(async (addressData: AddressData): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(route('api.delivery_zone.save_guest'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-XSRF-TOKEN': getCSRFToken(),
        },
        credentials: 'include',
        body: JSON.stringify(addressData),
      });

      if (!response.ok) {
        throw new Error('Error al guardar la direccion');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error desconocido';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    checkDeliveryZone,
    saveGuestAddress,
    isLoading,
    error,
  };
}
